import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import IconButton, {
  IconButtonSizes,
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import MonitoringFilterFieldNames from '@lib/enums/fieldNames/monitoringFilterFieldNames';
import { Option, Values } from '@lib/interfaces/form';
import _get from 'lodash/get';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATIC_COLORS } from '../constants';
import getFilterFieldNameByMonitoringEntity from '../utils/getFilterFieldNameByMonitoringEntity';
import getMonitoringEntity from '../utils/getMonitoringEntity';
import { UseQueryResult } from '../utils/useQueries';
import styles from './MonitoringFilter.module.scss';
import MonitoringFilterByEntityAddModalForm from './MonitoringFilterByEntityAddModalForm';

const STATIC_COLOR_KEYS = Object.keys(STATIC_COLORS);

type Props = {
  filterParams: Values;
  isLoading?: boolean;
  name: string;
  onUpdateFilterParams: (values: Values) => void;
  useQueriesResult: UseQueryResult;
};

export default function MonitoringFilterByEntityId({
  filterParams,
  isLoading,
  name,
  onUpdateFilterParams,
  useQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const items = _get(filterParams, name, []) as Option[];
  const ids = items.map((item) => item.value as string);
  const monitoringEntity = getMonitoringEntity({ filterParams });

  const onSiteRemove = (item: Option) => {
    onUpdateFilterParams({
      ...filterParams,
      [getFilterFieldNameByMonitoringEntity(
        monitoringEntity,
        MonitoringFilterFieldNames.FilterPresetId,
      )]: undefined,
      [name]: items.filter((s) => s.value !== item.value),
    });
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalSubmit = (values: Values) => {
    const site = _get(values, MonitoringFilterFieldNames.SiteToAdd);
    const sector = _get(values, MonitoringFilterFieldNames.SectorToAdd);
    const newEntity = site || sector;
    if (newEntity) {
      const newItems = [...items, newEntity];
      onUpdateFilterParams({
        [name]: newItems,
        [getFilterFieldNameByMonitoringEntity(
          monitoringEntity,
          MonitoringFilterFieldNames.FilterPresetId,
        )]: undefined,
        [MonitoringFilterFieldNames.LastUpdate]: new Date().toISOString(),
      });
    }
    handleModalClose();
  };

  return (
    <>
      <div className={styles.filterByEntityIdWrap}>
        {isLoading ? (
          <CircleLoader padding={0} />
        ) : (
          <div className={styles.filterByEntityId}>
            {items?.map((item, index) => (
              <div
                key={`${item.value}`}
                className={styles.filterByEntityIdChipWrap}
              >
                <Chip
                  label={item.label}
                  onClose={
                    items.length > 1 ? () => onSiteRemove(item) : undefined
                  }
                  size={ChipSizes.medium}
                  type={
                    STATIC_COLOR_KEYS[index]
                      ? (`chip-${STATIC_COLOR_KEYS[index]}` as ChipTypes)
                      : ChipTypes.chipBlue
                  }
                  leftIcon="circle"
                />
              </div>
            ))}
            <div>
              {items?.length >= 6 ? (
                <Tooltip body={t('item-length-limit-desc')}>
                  <IconButton
                    icon="add"
                    rounded
                    type={IconButtonTypes.SecondaryFilled}
                    size={IconButtonSizes.Medium}
                    disabled
                  />
                </Tooltip>
              ) : (
                <IconButton
                  icon="add"
                  rounded
                  type={IconButtonTypes.SecondaryFilled}
                  size={IconButtonSizes.Medium}
                  onClick={handleModalOpen}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <MonitoringFilterByEntityAddModalForm
        ids={ids}
        isOpen={isModalOpen}
        monitoringEntity={monitoringEntity}
        onCancel={handleModalClose}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        useQueriesResult={useQueriesResult}
      />
    </>
  );
}
